import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import { SEO } from '../../../utils/seo';

export default function SecurityEventOperationsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Event Security Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Holyhead, Anglesey",
        "addressRegion": "North Wales",
        "addressCountry": "GB"
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": "North Wales"
    },
    "description": "Professional, licensed security provision for venues, events and high-profile environments across North Wales. SIA-licensed operatives deliver exceptional security services.",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Event Security Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Door & Venue Security",
            "description": "Professional door supervision and venue security with fully licensed SIA operatives"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Wedding & Private Event Security",
            "description": "Discreet, professional security for weddings and private celebrations"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Close Protection Services",
            "description": "Elite close protection services for high-profile individuals and executives"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Emergency Same-Day Cover",
            "description": "Rapid response emergency security coverage available at short notice"
          }
        }
      ]
    }
  };

  const services = [
    {
      icon: 'ri-door-open-line',
      title: 'Door & Venue Security',
      description: 'Professional door supervision and venue security services with fully licensed SIA operatives ensuring safe, controlled access and maintaining order at your premises.',
      features: ['Licensed Door Supervisors', 'Crowd Management', 'ID Verification', 'Conflict Resolution']
    },
    {
      icon: 'ri-heart-line',
      title: 'Wedding & Private Event Security',
      description: 'Discreet, professional security for weddings and private celebrations, ensuring your special day runs smoothly while maintaining an elegant, unobtrusive presence.',
      features: ['Discreet Protection', 'Guest Management', 'Venue Coordination', 'Emergency Response']
    },
    {
      icon: 'ri-sun-line',
      title: 'Seasonal Holiday Park Support',
      description: 'Dedicated security solutions for holiday parks during peak seasons, providing comprehensive coverage to ensure guest safety and property protection throughout busy periods.',
      features: ['24/7 Patrols', 'Guest Assistance', 'Property Protection', 'Incident Management']
    },
    {
      icon: 'ri-team-line',
      title: 'Subcontract Cover for Security Companies',
      description: 'Reliable subcontract security services for other security companies requiring additional coverage, staffing support, or specialist expertise for their clients.',
      features: ['Flexible Staffing', 'Professional Standards', 'Rapid Deployment', 'Quality Assurance']
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Close Protection & Executive Safety',
      description: 'Elite close protection services for high-profile individuals and executives, providing discreet personal security with advanced threat assessment and risk mitigation.',
      features: ['Personal Protection', 'Threat Assessment', 'Advance Planning', 'Discreet Operations']
    },
    {
      icon: 'ri-car-line',
      title: 'Travel Escorting & Discreet Movements',
      description: 'Professional travel escort services ensuring safe, secure transportation and discreet movements for clients requiring enhanced security during transit.',
      features: ['Secure Transportation', 'Route Planning', 'Discreet Service', 'Risk Management']
    },
    {
      icon: 'ri-alarm-warning-line',
      title: 'Emergency Same-Day Cover',
      description: 'Rapid response emergency security coverage available at short notice when you need immediate professional security support for unexpected situations.',
      features: ['Same-Day Response', 'Emergency Deployment', 'Flexible Solutions', 'Professional Staff']
    }
  ];

  return (
    <>
      <SEO
        title="Event Security North Wales - SIA Licensed Door Supervisors - LEWIS SECURITY"
        description="Professional, licensed security provision for venues, events and high-profile environments across North Wales. SIA-licensed operatives deliver exceptional event security, door supervision, close protection and emergency same-day cover."
        keywords="event security North Wales, SIA licensed security, door supervisors, venue security, wedding security, close protection, emergency security cover, Holyhead Anglesey"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-red-900 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img 
              src="https://public.readdy.ai/ai/img_res/a454e6fbb4ae1ccb06cc8a3749a992ec.jpg"
              alt="Security & Event Operations"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-600 rounded-full mb-6">
                <i className="ri-shield-user-line text-4xl text-white"></i>
              </div>
              <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 font-poppins">
                Security & Event Operations
              </h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto font-inter leading-relaxed">
                Professional, licensed security provision for venues, events and high-profile environments across North Wales. Our experienced SIA-licensed operatives deliver exceptional security services tailored to your specific requirements.
              </p>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-3">Our Capabilities</h6>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 font-poppins">
                Comprehensive Event Security Services
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto font-inter">
                From intimate private events to large-scale venue operations, we provide professional security solutions with fully licensed, experienced personnel
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="group bg-white rounded-xl p-8 shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-red-600">
                  <div className="w-16 h-16 bg-red-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-red-600 transition-colors duration-300">
                    <i className={`${service.icon} text-3xl text-red-600 group-hover:text-white transition-colors duration-300`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">{service.title}</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed font-inter">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700 font-inter">
                        <i className="ri-check-line text-red-600 mr-2 text-lg"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-3">Professional Excellence</h6>
                <h2 className="text-4xl font-bold text-gray-900 mb-6 font-poppins">
                  Why Choose Our Event Security Services
                </h2>
                <p className="text-base text-gray-600 mb-6 font-inter leading-relaxed">
                  With years of experience in event security and venue operations across North Wales, we understand the unique challenges of protecting people, property, and reputations in dynamic environments.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-shield-check-line text-2xl text-red-600"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Fully Licensed Operatives</h4>
                      <p className="text-sm text-gray-600 font-inter">All our security personnel hold valid SIA licenses and undergo rigorous vetting and training to ensure the highest professional standards.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-time-line text-2xl text-red-600"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Rapid Response Capability</h4>
                      <p className="text-sm text-gray-600 font-inter">Emergency same-day cover available when you need immediate security support for unexpected situations or last-minute requirements.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-user-star-line text-2xl text-red-600"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Discreet & Professional</h4>
                      <p className="text-sm text-gray-600 font-inter">Our operatives are trained to provide effective security while maintaining a professional, unobtrusive presence appropriate to your event.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-red-600"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2 font-poppins">North Wales Coverage</h4>
                      <p className="text-sm text-gray-600 font-inter">Based in Holyhead, Anglesey, we provide comprehensive security coverage throughout North Wales with local knowledge and rapid deployment.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="w-full h-[600px] rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20security%20team%20at%20elegant%20venue%20entrance%20with%20door%20supervisors%20in%20formal%20attire%20managing%20guest%20access%20at%20upscale%20event%20showing%20professional%20security%20operations%20and%20crowd%20control%20with%20sophisticated%20lighting&width=800&height=1000&seq=security-event-why&orientation=portrait"
                    alt="Professional Event Security"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-8 rounded-xl shadow-xl max-w-xs">
                  <div className="text-4xl font-bold mb-2 font-poppins">15+</div>
                  <p className="text-sm font-inter">Years of experience in event security and venue operations across North Wales</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Industries Served */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-3">Sectors We Serve</h6>
              <h2 className="text-4xl font-bold text-gray-900 mb-4 font-poppins">
                Trusted Across Multiple Industries
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto font-inter">
                Our event security expertise spans diverse sectors throughout North Wales
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-building-line', name: 'Corporate Events' },
                { icon: 'ri-music-line', name: 'Concerts & Festivals' },
                { icon: 'ri-hotel-line', name: 'Hotels & Venues' },
                { icon: 'ri-football-line', name: 'Sports Events' },
                { icon: 'ri-store-line', name: 'Retail & Shopping' },
                { icon: 'ri-government-line', name: 'Public Sector' },
                { icon: 'ri-home-heart-line', name: 'Holiday Parks' },
                { icon: 'ri-vip-crown-line', name: 'VIP & Executive' }
              ].map((industry, index) => (
                <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-red-600 group cursor-pointer">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-colors duration-300">
                    <i className={`${industry.icon} text-2xl text-red-600 group-hover:text-white transition-colors duration-300`}></i>
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 font-poppins">{industry.name}</h4>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-red-600 to-red-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 font-poppins">
              Need Professional Event Security?
            </h2>
            <p className="text-xl text-red-50 mb-8 font-inter">
              Contact us today to discuss your security requirements. Emergency same-day cover available when needed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:07834561015"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2 text-xl"></i>
                Call 07834 561015
              </a>
              <a 
                href="/#contact"
                onClick={(e) => {
                  e.preventDefault();
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="inline-flex items-center justify-center px-8 py-4 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line mr-2 text-xl"></i>
                Get a Quote
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
